import React, { ReactElement, useState } from 'react';
import { GroupBase as DefaultGroupBase } from 'react-select';
import { useTheme } from 'styled-components';
import {
  HelpText,
  TextArea,
  TextAreaProps,
  TextInput,
  TextInputProps,
  Tooltip,
} from 'src/components/atoms';
import { StyledIcon } from 'src/components/foundations/foundations.styles';
import { FormInputVariant, FormState } from 'src/types';
import {
  DefaultOption,
  DropdownSelect,
  DropdownSelectProps,
} from '../DropdownSelect';
import {
  Container,
  FormInputDescription,
  FormInputLabel,
  FormInputLabelContainer,
  RequiredText,
} from './FormInput.styles';

interface DropdownProps<
  Option extends DefaultOption = DefaultOption,
  GroupBase extends DefaultGroupBase<Option> = DefaultGroupBase<Option>,
> extends DropdownSelectProps<Option, GroupBase> {
  variant: FormInputVariant.DROPDOWN;
}

interface FormTextAreaProps extends TextAreaProps {
  variant: FormInputVariant.AREA;
}

interface FormTextInputProps extends TextInputProps {
  variant: FormInputVariant.INPUT;
}

export type FormInputProps<
  Option extends DefaultOption = DefaultOption,
  GroupBase extends DefaultGroupBase<Option> = DefaultGroupBase<Option>,
> = {
  isRequired?: boolean;
  requiredText?: string;
  label?: string;
  description?: string;
  helpText?: string;
  'data-testid'?: string;
  tooltipText?: string;
  tooltipWidth?: string;
  disabled?: boolean;
  tabIndex?: number;
  enterKeyAction?: () => void;
  $zIndex?: number;
  $containerWidth?: string;
  $removeMarginBottom?: boolean;
} & (DropdownProps<Option, GroupBase> | FormTextInputProps | FormTextAreaProps);

const InnerFormInput = <
  Option extends DefaultOption = DefaultOption,
  GroupBase extends DefaultGroupBase<Option> = DefaultGroupBase<Option>,
>(
  props: FormInputProps<Option, GroupBase>,
  ref?: React.Ref<HTMLInputElement>,
) => {
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [root, setRoot] = useState<HTMLElement | null>(null);
  const theme = useTheme();

  return (
    <>
      <Container
        fitToContents={(props as DropdownProps).routing}
        $containerWidth={props.$containerWidth}
      >
        {props.label && (
          <FormInputLabelContainer
            hasMarginBottom={props.variant === FormInputVariant.DROPDOWN}
          >
            <FormInputLabel
              disabled={props.state === FormState.DISABLED}
              htmlFor={props['data-testid']}
            >
              {props.label}{' '}
              {props.isRequired ? (
                <RequiredText disabled={props.state === FormState.DISABLED}>
                  {props.requiredText ?? '(Required)'}
                </RequiredText>
              ) : (
                ''
              )}
            </FormInputLabel>
            {props.tooltipText && (
              <StyledIcon
                className={'uil uil-info-circle'}
                onMouseOver={() => {
                  setTooltipVisible(true);
                }}
                onMouseOut={() => setTooltipVisible(false)}
                ref={setRoot}
                color={
                  tooltipVisible
                    ? theme.atlas.semantic.color.action.link.default
                    : theme.atlas.semantic.color.type.default
                }
                size="20px"
                data-testid="form-input-information"
              />
            )}
          </FormInputLabelContainer>
        )}
        {props.variant === FormInputVariant.AREA && <TextArea {...props} />}
        {props.variant === FormInputVariant.INPUT && (
          <TextInput
            {...props}
            forwardRef={ref ?? props.forwardRef}
            onKeyDown={(e) => {
              if (props.enterKeyAction && e.key === 'Enter') {
                e.preventDefault();
                props.enterKeyAction();
              }
            }}
          />
        )}
        {props.variant === FormInputVariant.DROPDOWN && (
          <DropdownSelect<Option, GroupBase>
            {...props}
            disabled={props.state === FormState.DISABLED}
          />
        )}

        {props.helpText &&
          props.state &&
          [FormState.SUCCESS, FormState.ERROR, FormState.WARNING].includes(
            props.state,
          ) && (
            <HelpText
              state={props.state}
              helpText={props.helpText}
              data-testid={`${props['data-testid']}-help-text`}
              $margin="0 0 8px 0"
            />
          )}

        {props.description && (
          <FormInputDescription
            hasMarginTop={props.variant === FormInputVariant.DROPDOWN}
          >
            {props.description}
          </FormInputDescription>
        )}
      </Container>
      {props.tooltipText && tooltipVisible && root && (
        <Tooltip
          text={props.tooltipText}
          variant="down"
          root={root}
          data-testid="form-input-tooltip"
          width={props.tooltipWidth}
        />
      )}
    </>
  );
};

export const FormInput = React.forwardRef(InnerFormInput) as <
  Option extends DefaultOption = DefaultOption,
  GroupBase extends DefaultGroupBase<Option> = DefaultGroupBase<Option>,
>(
  props: FormInputProps<Option, GroupBase> & {
    ref?: React.Ref<HTMLInputElement>;
  },
) => ReactElement;
